import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import {
	checkPortalSessionShape,
	type PortalSessionShapeFailure,
} from '@liquidweb-dev/lw-chat-js';

import type { AppConfigI } from 'appConfigs/example';
import appConfigSelectors from 'modules/appConfig/selectors';
import useAuthenticated from 'modules/oidc/useAuthenticated';

import selectAgentVariant from './selectAgentVariant';

export type MethodicConfig = NonNullable<AppConfigI['methodic']>;

/** Fixed per page load so every import of the widget module shares one URL. */
const WIDGET_VERSION = String(Date.now());

/**
 * Appends a per-page-load version to the widget module URL so browsers that
 * cached an older `client.js` fetch the current one.
 * @param moduleUrl the configured Methodic widget module URL
 */
const withWidgetVersion = (moduleUrl: string) => {
	if (!moduleUrl) return moduleUrl;
	const url = new URL(moduleUrl, window.location.href);
	url.searchParams.set('v', WIDGET_VERSION);
	return url.toString();
};

export interface UseMethodicSessionResult {
	/** The raw `methodic` app-config block, when one is configured. */
	methodic: MethodicConfig | undefined;
	/** The widget module URL to import, cache-busted per page load. */
	moduleUrl: string;
	/** A Methodic channel key is configured for this environment. */
	isEnabled: boolean;
	/** Configured, and the current user is authenticated. */
	isActive: boolean;
	/** The stored OIDC session passes Methodic's `portalAuth` shape check. */
	hasValidSessionShape: boolean;
	/** Every reason the shape check failed, when it did. */
	failures: PortalSessionShapeFailure[];
	/** Whether Methodic is fully usable right now: configured, authenticated,
	 * and holding a session Methodic will actually accept. Does not account
	 * for whether the widget bundle has finished loading. */
	isUsable: boolean;
	issuer: string;
	audience: string;
	/** The account's assigned agent variant, when variants are configured. */
	agentVariant: string | undefined;
}

/**
 * Single source of truth for whether Methodic chat is usable right now.
 */
export default function useMethodicSession(): UseMethodicSessionResult {
	const config = useSelector(appConfigSelectors.getAppConfig);
	const { methodic, openId } = config || {};
	const isAuthenticated = useAuthenticated();
	const { user } = useAuth();

	const isEnabled = Boolean(methodic?.apiKey);
	const isActive = isEnabled && isAuthenticated;
	const issuer = openId?.authority ?? '';
	const audience = openId?.client_id ?? '';

	const { valid: hasValidSessionShape, failures } = useMemo(
		() =>
			isActive
				? checkPortalSessionShape(user, issuer, audience)
				: { valid: false, failures: [] },
		[isActive, user, issuer, audience],
	);

	return {
		methodic,
		moduleUrl: withWidgetVersion(methodic?.moduleUrl ?? ''),
		isEnabled,
		isActive,
		hasValidSessionShape,
		failures,
		isUsable: isActive && hasValidSessionShape,
		issuer,
		audience,
		agentVariant: selectAgentVariant(user?.profile?.group, methodic?.variants),
	};
}
